import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2, Plus } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { DataTable, type RowKey } from "@/components/ui/data-table"
import { Dialog, DialogTrigger } from "@/components/ui/dialog"
import { SectionCard } from "@/components/ui/section-card"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { useToast } from "@/components/ui/toast"
import { bannerFor } from "@/lib/api-error-message"
import { COMING_SOON, FEATURES } from "@/lib/features"
import { useHasLoadedOnce, useTableUrlState, type TableSort } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"

import {
  clientUsersQuery,
  clientsQuery,
  primaryClientContactCandidatesQuery,
  staffByIdQuery,
  toPrimaryClientContact,
} from "./api"
import { buildClientsColumns } from "./clients-columns"
import {
  filterClients,
  type ClientRow,
  type ClientUserOption,
  type StaffOption,
} from "./clients-data"
import { NewEngagementDialog } from "./clients-dialogs"
import { buildClientsFilterSchema, filtersToQuery } from "./clients-filters"
import { ExpandedEngagement } from "./clients-expanded"
import { ClientsLoadError } from "./clients-load-error"
import { ClientsPager } from "./clients-pager"
import { ClientsTableEmpty } from "./clients-table-empty"
import { useEngagementActions } from "./hooks/use-engagement-actions"

const EMPTY_USERS: ClientUserOption[] = []

const NO_PRIMARY_CLIENT_CONTACT_CANDIDATES: StaffOption[] = []

const EMPTY_ROWS: ClientRow[] = []

const SORT_OPTIONS: SortOption[] = [
  { id: "createdAt", label: "Since", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "name", label: "Client name", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "reviewCount", label: "Reviews", ascLabel: "Fewest first", descLabel: "Most first" },
  { id: "members", label: "Members", ascLabel: "Fewest first", descLabel: "Most first" },
]
const SORT_FIELDS: readonly string[] = SORT_OPTIONS.map((o) => o.id)
const DEFAULT_SORT: TableSort = { field: "createdAt", dir: "desc" }

export function ClientsPage(): JSX.Element {
  const schema = React.useMemo(() => buildClientsFilterSchema(), [])
  const { toast } = useToast()
  const searchRef = React.useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = React.useState<Set<RowKey>>(new Set())
  const [createOpen, setCreateOpen] = React.useState(false)

  // GET /clients + /users are both admin-only server-side. The users roster is
  // fetched once (small, static-ish); the engagement page is server-paginated.
  const { data: clientUsers = EMPTY_USERS, error: loadError } = useQuery(clientUsersQuery())
  const { data: primaryClientContactCandidates = NO_PRIMARY_CLIENT_CONTACT_CANDIDATES } = useQuery(
    primaryClientContactCandidatesQuery(),
  )
  const { data: staffById, isPending: isStaffRosterLoading } = useQuery(staffByIdQuery())

  const resolvePrimaryClientContact = React.useCallback(
    (row: ClientRow) => toPrimaryClientContact(row.primaryClientContactId, staffById),
    [staffById],
  )

  useSlashShortcut(searchRef)

  // The query lives in the URL, as on Users (see lib/table/use-table-url-state.ts), so a
  // filtered view survives a reload and can be shared, and the back button steps through it.
  // React Query fetches whatever the URL says.
  const { state: url, actions } = useTableUrlState({
    schema,
    sortFields: SORT_FIELDS,
    defaultSort: DEFAULT_SORT,
  })
  const qc = useQueryClient()
  const clientsResult = useQuery(
    clientsQuery(qc, {
      page: url.page,
      pageSize: url.pageSize,
      search: url.committedSearch,
      sort: url.sort,
    }),
  )
  const state = {
    ...url,
    rows: clientsResult.data?.items ?? EMPTY_ROWS,
    total: clientsResult.data?.total,
  }

  // A shared or stale link can point past the last page (`?page=9` after engagements went).
  // The backend answers with an empty page; step back to the real last one, as Users does.
  // `replace`, because this is a correction and not a page the reader asked for: pushed, it
  // leaves the out-of-range page one Back away, which corrects forward again.
  const servedTotal = clientsResult.data?.total
  const servedEmpty = clientsResult.data?.items.length === 0
  const { setPage } = actions
  React.useEffect(() => {
    if (servedTotal === undefined || servedTotal === 0 || !servedEmpty) return
    const lastPage = Math.max(1, Math.ceil(servedTotal / url.pageSize))
    if (url.page > lastPage) setPage(lastPage, { replace: true })
  }, [servedTotal, servedEmpty, url.page, url.pageSize, setPage])

  const isTableDataLoading = clientsResult.isPending || isStaffRosterLoading
  const hasLoadedOnce = useHasLoadedOnce(isTableDataLoading)

  const {
    memberActionsFor,
    createEngagement,
    isCreatingEngagement,
    changePrimaryClientContactFor,
    saveProfileFor,
  } = useEngagementActions({
    goToFirstPage: () => {
      setPage(1)
    },
    clientUsers,
    primaryClientContactCandidates,
    closeNewEngagementDialog: () => {
      setCreateOpen(false)
    },
  })

  // Local axes (status, hasPrimary, createdAt) still apply within the current page.
  // Server-side wiring for these is task #26/#38 territory.
  const filteredRows = React.useMemo(
    () => filterClients(state.rows, filtersToQuery(state.filters)),
    [state.rows, state.filters],
  )

  const toggleExpand = React.useCallback((key: RowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const columns = React.useMemo(
    () => buildClientsColumns(resolvePrimaryClientContact),
    [resolvePrimaryClientContact],
  )

  // The committed search, not the draft: the empty state describes the rows on screen, which
  // were fetched for what is in the URL.
  const filtered = state.committedSearch.length > 0 || state.filterCount > 0

  // A failed list is not an empty one. Without this the table falls through to "No
  // engagements yet" and invites creating one — an answer someone would act on. An aborted
  // request (navigation, a newer query) has no title and is not worth showing.
  const listError = clientsResult.isError ? bannerFor(clientsResult.error, "engagements") : null
  const listFailed = listError !== null && listError.title !== ""
  const { refetch: refetchClients } = clientsResult

  const newEngagementAction = FEATURES.newEngagement ? (
    <Dialog
      open={createOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen && isCreatingEngagement) return
        setCreateOpen(isOpen)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="primary" size="sm" leadingIcon={<Plus />}>
          New engagement
        </Button>
      </DialogTrigger>
      <NewEngagementDialog
        primaryClientContactCandidates={primaryClientContactCandidates}
        onSubmit={createEngagement}
      />
    </Dialog>
  ) : (
    <Button
      variant="primary"
      size="sm"
      leadingIcon={<Plus />}
      onClick={() => {
        toast(COMING_SOON)
      }}
    >
      New engagement
    </Button>
  )

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      {loadError && <ClientsLoadError error={loadError} />}

      {/* One card holding search, filters, table and pager, as on Users. As tall as its
          rows: the pager sits under the last row, and a long page scrolls inside the card. */}
      <SectionCard
        icon={<Building2 className="size-4" aria-hidden="true" />}
        title="Clients"
        subtitle="Engagements that scope a client login to its own reviews."
        count={clientsResult.data?.total}
        countFailed={listFailed}
        action={newEngagementAction}
      >
        <div className="shrink-0 border-t border-border-soft px-5 py-3">
          <TableToolbar
            search={state.search}
            onSearchChange={actions.setSearch}
            searchRef={searchRef}
            searchPlaceholder="Search by client, contact name or email"
            searchAriaLabel="Search engagements"
            searchShortcut="/"
            filterSchema={schema}
            filterValues={state.filters}
            onFiltersChange={actions.setFilters}
            onClearAllFilters={actions.clearAllFilters}
            sortOptions={SORT_OPTIONS}
            sort={state.sort}
            onSortChange={actions.setSort}
          />
          <div className="mt-3">
            <ActiveFilterChips
              schema={schema}
              values={state.filters}
              onRemove={actions.clearFilter}
              onClearAll={actions.clearAllFilters}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t border-border-soft">
          <DataTable<ClientRow>
            label="Engagements"
            columns={columns}
            rows={listFailed ? EMPTY_ROWS : filteredRows}
            rowKey={(r) => r.id}
            expandedKeys={expanded}
            onToggleExpand={toggleExpand}
            renderExpanded={(row) => (
              <ExpandedEngagement
                row={row}
                users={clientUsers}
                memberActions={memberActionsFor(row)}
                primaryClientContact={resolvePrimaryClientContact(row)}
                primaryClientContactCandidates={primaryClientContactCandidates}
                onPrimaryClientContactChange={changePrimaryClientContactFor(row)}
                onSaveProfile={saveProfileFor(row)}
              />
            )}
            stickyHeader
            loading={isTableDataLoading && !hasLoadedOnce}
            className="min-w-[1100px] rounded-none border-0"
            empty={
              <ClientsTableEmpty
                failed={listFailed}
                error={listError}
                filtered={filtered}
                retrying={clientsResult.isFetching}
                onRetry={() => {
                  void refetchClients()
                }}
                onClearFilters={() => {
                  // `clearSearch`, not `setSearch("")`: the draft alone would leave `q` in the
                  // URL for another 300 ms, so the empty state would stay "filtered" and the
                  // page would fetch the search it was just asked to drop.
                  actions.clearSearch()
                  actions.clearAllFilters()
                }}
                onNewEngagement={() => {
                  if (FEATURES.newEngagement) setCreateOpen(true)
                  else toast(COMING_SOON)
                }}
              />
            }
          />
        </div>

        <ClientsPager
          total={state.total}
          failed={listFailed}
          page={state.page}
          pageSize={state.pageSize}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setPageSize}
        />
      </SectionCard>
    </div>
  )
}
